import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus, Sparkles } from "lucide-react";

interface BookingLite {
  id: string;
  user_id: string;
  service_category?: string;
  price: number;
  duration: string;
  service_name: string;
}

interface Props {
  booking: BookingLite | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True for admin caller — admin add-ons are auto-confirmed (no pending step) */
  asAdmin?: boolean;
  onSubmitted?: () => void;
}

interface AddonRow {
  id: string;
  name: string;
  price: number;
  duration: string;
}

const AddonRequestDialog = ({ booking, open, onOpenChange, asAdmin = false, onSubmitted }: Props) => {
  const { toast } = useToast();
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelected(new Set());
    }
  }, [open]);

  // Resolve category id from booking.service_category (which is the title text)
  useEffect(() => {
    if (!open || !booking) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: cat } = await supabase
        .from("service_categories")
        .select("id, title")
        .eq("title", booking.service_category)
        .maybeSingle();
      if (cancelled) return;
      if (!cat) {
        setAddons([]);
        setCategoryId(null);
        setLoading(false);
        return;
      }
      setCategoryId(cat.id);
      const { data, error } = await supabase
        .from("service_addons")
        .select("id, name, price, duration")
        .eq("category_id", cat.id)
        .order("sort_order");
      if (cancelled) return;
      if (error) {
        toast({ title: "Couldn't load add-ons", description: error.message, variant: "destructive" });
      }
      setAddons((data || []) as AddonRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, booking?.id]);

  const totals = useMemo(() => {
    const chosen = addons.filter((a) => selected.has(a.id));
    const price = chosen.reduce((s, a) => s + Number(a.price || 0), 0);
    return { count: chosen.length, price };
  }, [addons, selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!booking || selected.size === 0) return;
    setSubmitting(true);
    const { data: { user } } = await supabase.auth.getUser();

    const rows = addons
      .filter((a) => selected.has(a.id))
      .map((a) => ({
        booking_id: booking.id,
        user_id: booking.user_id,
        addon_id: a.id,
        name: a.name,
        price: a.price,
        duration: a.duration,
        status: asAdmin ? "confirmed" : "pending",
        requested_by: asAdmin ? "admin" : "client",
        requested_by_user: user?.id ?? null,
        confirmed_by: asAdmin ? user?.id ?? null : null,
      }));

    const { data: inserted, error } = await supabase
      .from("booking_addons")
      .insert(rows)
      .select("id");

    if (error) {
      toast({ title: asAdmin ? "Failed to add" : "Request failed", description: error.message, variant: "destructive" });
      setSubmitting(false);
      return;
    }

    // Fire notification: admin add-ons go straight to confirmed; client add-ons are pending
    try {
      await supabase.functions.invoke("notify-addon-request", {
        body: {
          booking_id: booking.id,
          addon_ids: (inserted || []).map((r: any) => r.id),
          event: asAdmin ? "confirmed" : "pending",
        },
      });
    } catch (e) {
      console.error("notify failed", e);
    }

    toast({
      title: asAdmin ? "Add-ons added" : "Request sent",
      description: asAdmin
        ? `${rows.length} add-on${rows.length === 1 ? "" : "s"} added to booking.`
        : `${rows.length} add-on${rows.length === 1 ? "" : "s"} pending admin confirmation.`,
    });
    setSubmitting(false);
    onOpenChange(false);
    onSubmitted?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border/50 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif italic text-xl">
            <Sparkles size={16} className="text-primary" />
            {asAdmin ? "Add-on Service" : "Request Add-ons"}
          </DialogTitle>
          <DialogDescription className="text-xs tracking-wider">
            {booking?.service_name} · {booking?.service_category}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[55vh] overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
            </div>
          ) : addons.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">
              No add-ons available for this service.
            </p>
          ) : (
            addons.map((a) => {
              const isSelected = selected.has(a.id);
              return (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => toggle(a.id)}
                  className={`w-full flex items-center justify-between gap-3 p-3 border text-left transition-colors ${
                    isSelected
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-border"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Checkbox checked={isSelected} className="pointer-events-none" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{a.name}</p>
                      <p className="text-[11px] text-muted-foreground">{a.duration}</p>
                    </div>
                  </div>
                  <p className="text-sm font-medium tabular-nums">+${Number(a.price).toFixed(2)}</p>
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border/30 pt-3 text-xs tracking-wider uppercase">
          <span className="text-muted-foreground">{totals.count} selected</span>
          <span className="font-medium">+${totals.price.toFixed(2)}</span>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || selected.size === 0} className="gap-1">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {asAdmin ? "Add" : "Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddonRequestDialog;
